import { NorthEast } from '@mui/icons-material';
import { Button, CircularProgress, styled, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { generateConflictReport } from '../../../../services/dealGPT';
import { ProjectNotesStatus } from '../../../../Types/dealGPT';
import { SourceDocument } from '../../../../Types/indexes';
import { conflictReportValidationPathUrl } from '../../../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import ConflictReportSummary from './ConflictReportSummary';

const ENABLED_COLOR = '#FF3A2F';
const DISABLED_COLOR = '#AAAAAA';
const DISABLED_BORDER = '#CCCCCC';
const DISABLED_TEXT = '#999999';

interface ConflictReportType {
  content?: {
    conflicts: Array<{
      type: string;
      severity: string;
      conflict: string;
      sourceDocuments: SourceDocument[];
    }>;
    status?: string;
  };
  status?: string;
}

const ReportItem = styled('div')<{ disabled?: boolean }>(({ disabled }) => ({
  border: `1px solid ${disabled ? DISABLED_BORDER : ENABLED_COLOR}`,
  padding: '15px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  opacity: disabled ? 0.6 : 1,
}));

const ReportDetails = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const ReportTitle: React.FC<{ disabled?: boolean; children: React.ReactNode }> = ({
  disabled = false,
  children,
}) => (
  <Typography
    sx={{
      fontSize: '16px',
      fontWeight: 600,
      color: disabled ? DISABLED_TEXT : ENABLED_COLOR,
    }}
  >
    {children}
  </Typography>
);

const ReportSubtitle: React.FC<{ disabled?: boolean; children: React.ReactNode }> = ({
  disabled = false,
  children,
}) => (
  <Typography
    sx={{
      fontSize: '14px',
      color: disabled ? DISABLED_TEXT : ENABLED_COLOR,
    }}
  >
    {children}
  </Typography>
);

const ProcessButton: React.FC<{
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  loading?: boolean;
}> = ({ disabled = false, onClick, children, loading = false }) => (
  <Button
    onClick={onClick}
    disabled={disabled || loading}
    endIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <NorthEast />}
    sx={{
      backgroundColor: disabled ? DISABLED_COLOR : ENABLED_COLOR,
      color: 'white',
      '&:hover': {
        backgroundColor: disabled ? DISABLED_COLOR : '#E43327',
      },
      textTransform: 'none',
      padding: '6px 16px',
      '&.Mui-disabled': {
        color: '#FFFFFF',
        backgroundColor: loading ? ENABLED_COLOR : DISABLED_COLOR,
      },
      borderRadius: '0',
    }}
  >
    {loading ? 'Processing...' : children}
  </Button>
);

interface ConflictReportProps {
  projectId: string;
}

const ConflictReport = ({ projectId }: ConflictReportProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [report, setReport] = useState<ConflictReportType>({ content: { conflicts: [] } });
  const analytics = useAnalytics();

  const { data: projectStatus } = useFetchData<ProjectNotesStatus>({
    urlPath: conflictReportValidationPathUrl(projectId),
  });

  const isComponentEnabled = projectStatus?.hasNotes === true;

  const formattedLastRun = projectStatus?.lastRun
    ? new Date(projectStatus.lastRun).toLocaleString()
    : '';

  const handleProcess = useCallback(async () => {
    // Start button loading state
    setIsButtonLoading(true);

    // Track analytics
    analytics.event(
      AnalyticsEvent.WM_IA_D_CONFLICT_REPORT_PROCESS,
      {
        id: projectId,
      },
      {
        projectId: projectId,
      }
    );

    setTimeout(() => {
      setModalOpen(true);
      setIsLoading(true);
    }, 10000);

    try {
      const result = await generateConflictReport(projectId);
      setReport(result);
      // Once process completes, update loading states
      setIsLoading(false);
      setIsButtonLoading(false);
    } catch (error) {
      console.error('Error generating conflict report:', error);
      setIsButtonLoading(false);
      setIsLoading(false);
      setReport({ status: 'Error' });
    }
  }, [projectId, analytics]);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Tooltip
        title="You must have at least one site visit note for an analysis scope area defined to process conflict highlight report"
        disableHoverListener={isComponentEnabled}
        disableFocusListener={isComponentEnabled}
        disableTouchListener={isComponentEnabled}
      >
        <ReportItem disabled={!isComponentEnabled}>
          <ReportDetails>
            <ReportTitle disabled={!isComponentEnabled}>Conflict Review</ReportTitle>
            <ReportSubtitle disabled={!isComponentEnabled}>
              Last Run: {formattedLastRun}
            </ReportSubtitle>
          </ReportDetails>
          <ProcessButton
            disabled={!isComponentEnabled}
            onClick={handleProcess}
            loading={isButtonLoading}
          >
            Process
          </ProcessButton>
        </ReportItem>
      </Tooltip>
      <ConflictReportSummary
        open={modalOpen}
        onClose={handleCloseModal}
        projectId={projectId}
        report={report}
        isLoading={isLoading}
      />
    </>
  );
};

export default ConflictReport;
